import { useSize } from 'ahooks'
import cn from 'classnames'
import type { OverlayScrollbarsComponent } from 'overlayscrollbars-react'
import { useEffect, useMemo, useRef } from 'react'
import React from 'react'

import AbsolutePortal from 'components/Editor/components/AbsolutePortal'
import { ABSOLUTE_PORTAL_GAP } from 'components/Editor/components/AbsolutePortal/constants'
import { LayoutScroll } from 'components/LayoutPage'
import { stopPropagation } from 'constants/commonConstans'
import { PreviewMode } from 'services/Store/Project/enums'

import { useTableContext } from '../../TableContext'
import { isElementInViewport } from '../../utils'
import OptionsMenu from '../OptionsMenu'
import Row from '../Row'
import TableDragLayer from '../TableDragLayer'
import * as s from './Table.module.scss'

const Table = () => {
  const {
    cellNodes,
    tableRef,
    tableValue,
    setDndControlsCoordinates,
    selectedCells,
    mode,
    styles,
  } = useTableContext()
  const containerRef = useRef<HTMLDivElement>(null)
  const scrollRef = useRef<OverlayScrollbarsComponent>(null)
  const selectedAreaRef = useRef<HTMLDivElement>(null)
  const { height: tableHeight } = useSize(tableRef) || {}

  const containerWidth = containerRef.current?.parentElement?.parentElement?.clientWidth
  const tableWidth = tableRef.current?.clientWidth
  const selectedZoneRectStyle = useMemo(() => {
    if (!selectedCells.length) {
      return undefined
    }

    const { row: startRow, col: startCol } = selectedCells[0]
    const { row: endRow, col: endCol } = selectedCells[selectedCells.length - 1]

    if (!cellNodes?.[startRow]?.[startCol] || !cellNodes?.[endRow]?.[endCol]) {
      return undefined
    }

    const startCell = cellNodes?.[startRow]?.[startCol]
    const endCell = cellNodes?.[endRow]?.[endCol]

    const startCellRect = startCell.getBoundingClientRect()
    const endCellRect = endCell.getBoundingClientRect()

    const tableRect = tableRef.current?.getBoundingClientRect()

    if (!tableRect) {
      return undefined
    }

    const top = startCellRect.top - tableRect.top
    const left = startCellRect.left - tableRect.left
    const width = endCellRect.right - startCellRect.left
    const height = endCellRect.bottom - startCellRect.top

    return { top, left, width, height }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCells, tableHeight])

  useEffect(() => {
    const instance = scrollRef.current?.osInstance()
    const viewport = instance?.getElements()?.viewport as HTMLElement | undefined
    if (viewport) {
      viewport.tabIndex = 0
    }
  }, [])

  useEffect(() => {
    const instance = scrollRef.current?.osInstance()
    const scrollContainer = scrollRef.current?.osTarget()
    if (instance && selectedAreaRef.current && scrollContainer) {
      if (!isElementInViewport(selectedAreaRef.current, scrollContainer)) {
        instance.scroll({ el: selectedAreaRef.current, block: 'nearest', scroll: 'ifneeded' }, 100)
      }
    }
  }, [selectedCells, tableRef.current?.clientHeight])

  const isPdf = mode.previewMode === PreviewMode.pdf

  if (isPdf) {
    const hasScroll = containerWidth && tableWidth && tableWidth > containerWidth
    const scaleStylesWithScrollTable = {
      ...(hasScroll && {
        transform: `scale(${containerWidth / tableWidth})`,
        transformOrigin: 'top left',
      }),
    }

    return (
      <div className={cn(s.tableContainer, s.pdf)} ref={containerRef}>
        <table className={s.table} ref={tableRef} style={scaleStylesWithScrollTable}>
          <tbody>
            {tableValue.cells.map((_cell, i) => (
              <React.Fragment key={i}>
                <Row index={i} />
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  return (
    <>
      <div className={s.wrapper} style={styles?.effects}>
        <LayoutScroll ref={scrollRef} sizeAutoCapable>
          <div
            className={cn(s.tableContainer, {
              [s.center]: mode.previewMode === PreviewMode.demo,
            })}
            onWheelCapture={stopPropagation}
            ref={containerRef}
          >
            <table
              className={s.table}
              onMouseLeave={() => setDndControlsCoordinates({ x: null, y: null })}
              ref={tableRef}
            >
              <tbody>
                {tableValue.cells.map((_cell, i) => (
                  <Row index={i} key={i} />
                ))}
              </tbody>
            </table>

            <TableDragLayer />

            {!!selectedCells.length && (
              <div className={s.selectedArea} ref={selectedAreaRef} style={selectedZoneRectStyle} />
            )}
          </div>
        </LayoutScroll>
      </div>
      {Boolean(selectedCells.length) && (
        <AbsolutePortal
          name='tableOptions'
          placement='topRight'
          translateX='-100%'
          translateY={`calc(-100% - ${ABSOLUTE_PORTAL_GAP}px)`}
          zIndex={1}
        >
          <OptionsMenu containerWidth={containerWidth} />
        </AbsolutePortal>
      )}
    </>
  )
}

export default Table
